"use client";

import { useEffect, useState } from "react";
import Script from "next/script";
import { GoogleTagManager } from "@next/third-parties/google";
import Cookies from "js-cookie";

import { getCookie, CookieName } from "@/utils/cookie.helper";
import { env } from "@/env.mjs";

// ----------------------------------------------------------------------------
// GTM + Google Consent Mode v2.
//
// What changed vs the previous version:
//   - GTM now mounts ALWAYS (not gated on consent). This is required by
//     Consent Mode v2: GA4 needs to receive cookieless pings pre-consent
//     so it can ML-model the conversions Google Ads is missing attribution
//     for. Without this, denied users contribute zero data.
//   - A `beforeInteractive` inline script sets default consent to denied
//     for ad_* + analytics_storage BEFORE GTM loads. Functionality +
//     security storage stay granted (needed for the site to work).
//   - After mount, we read the existing analytics cookie. If accepted,
//     we push a consent UPDATE that flips ad_* + analytics_storage to
//     granted — GA + Ads switch from "modeled" to direct measurement.
//   - We also expose a global `__cbConsentUpdate(true|false)` helper so
//     the cookie banner (Mandatly) can call it on Accept / Decline
//     without forcing a page reload.
//
// Privacy posture: raw user PII is never sent client-side. With consent
// denied, cookieless pings include only event names + non-PII params.
// ----------------------------------------------------------------------------

const removeGoogleAnalyticsCookies = () => {
  const cookies = Object.keys(Cookies.get());
  cookies.forEach((cookie) => {
    if (cookie.startsWith("_ga")) {
      Cookies.remove(cookie);
    }
  });
};

// Inline script — must run BEFORE the GTM library so the default consent
// state is in the dataLayer by the time GA4 reads it. Hence
// `strategy="beforeInteractive"`.
const consentDefaultScript = `
  window.dataLayer = window.dataLayer || [];
  function gtag(){dataLayer.push(arguments);}
  window.gtag = window.gtag || gtag;
  gtag('consent', 'default', {
    'ad_storage': 'denied',
    'ad_user_data': 'denied',
    'ad_personalization': 'denied',
    'analytics_storage': 'denied',
    'functionality_storage': 'granted',
    'security_storage': 'granted',
    'wait_for_update': 500
  });
  // Expose a helper the cookie banner can call to flip consent without
  // needing to reload the page. Accepts true (granted) or false (denied).
  window.__cbConsentUpdate = function(accepted) {
    var state = accepted ? 'granted' : 'denied';
    gtag('consent', 'update', {
      'ad_storage': state,
      'ad_user_data': state,
      'ad_personalization': state,
      'analytics_storage': state
    });
  };
`;

const GoogleTagManagerScript = () => {
  const [consentResolved, setConsentResolved] = useState(false);

  useEffect(() => {
    // If the user hasn't accepted analytics, scrub any stale _ga cookies
    // (from a previous session or another origin) — same hygiene step
    // we had before.
    if (!getCookie(CookieName.ANALYSIS)) {
      removeGoogleAnalyticsCookies();
    }

    // Read the cookie value and push a consent UPDATE matching its state.
    // This runs after the default-deny script has loaded so the gtag()
    // function is guaranteed to exist.
    const accepted = Boolean(getCookie(CookieName.ANALYSIS));
    const win = window as unknown as {
      __cbConsentUpdate?: (accepted: boolean) => void;
    };
    if (typeof win.__cbConsentUpdate === "function") {
      win.__cbConsentUpdate(accepted);
    }
    setConsentResolved(true);
  }, []);

  return (
    <>
      {/* Default-deny consent — must load before GTM. */}
      <Script
        id="gtag-consent-default"
        strategy="beforeInteractive"
      >
        {consentDefaultScript}
      </Script>

      {/* GTM always mounts. With consent denied, GA sends cookieless
          modeled pings; with consent granted, full direct measurement.
          `consentResolved` just prevents a Mantine SSR hydration warning. */}
      {consentResolved && !!env.NEXT_PUBLIC_GOOGLE_TAG_MANAGER_ID && (
        <GoogleTagManager gtmId={env.NEXT_PUBLIC_GOOGLE_TAG_MANAGER_ID} />
      )}
    </>
  );
};

export default GoogleTagManagerScript;
